import get from 'lodash/get';
import history from '@AppRoutes/history';
import store from '@Store';
import Breadcrumb from '@Services/Breadcrumb';
import { structureContainerIds } from '@Pages/StructureEditor/StructureEditorConstants';
import { keyifyParamsForFiltersRequest } from '@Containers/ResultsFilters/ResultsFiltersComponents/ListOfFilters/ListOfFiltersSelectors';
import { getCombinedRouteParams } from '@AppRoutes/AppRoutesSelectors';
import { databaseId as databaseIdSelector } from '@Selectors/StateParamsSelectors';
import { updateFilterById } from '@Containers/ResultsFilters/ResultsFiltersComponents/ListOfFilters/ListOfFiltersActions';
import { fetchAndCreateInitialOptionsCopy } from '@Containers/StructureOptions/StructureOptionsActions';
import StructureContainers from '@Services/StructureContainers';
import { selectedOptionsNameValueObjectsArray as selectedOptionsNameValueObjectsArraySelector } from '@Containers/StructureOptions/StructureOptionsSelecors';
import { updateStructureOptionsDialog as updateStructureOptionsDialogAction } from '@Containers/StructureOptions/StructureOptionsDialog/StructureOptionsDialogActions';
import { setDrawingType } from './StructureFilterConstants';
import { drawing as drawingSelector } from './StructureFilterSelectors';

export function setDrawing(key, data) {
  return {
    type: setDrawingType,
    payload: {
      key,
      data,
    },
  };
}

export function setStructureOptions(key, options) {
  return fetchAndCreateInitialOptionsCopy(key, options);
}

export function unselectFilter(key) {
  return updateFilterById(key, 'YY', {
    selected: false,
  });
}

export function setExpandedFilter(key) {
  return updateFilterById(key, 'YY', {
    expanded: true,
  });
}

export function setSelectedAndExpandedFilter(key) {
  return updateFilterById(key, 'YY', {
    selected: true,
    expanded: true,
  });
}

export function getStructureFilterFromBreadCrumb(state, filterId) {
  const databaseId = databaseIdSelector(state);

  const breadcrumb = Breadcrumb.getActiveBreadcrumbForDb(databaseId);
  let filtersFromBreadCrumb;

  if (breadcrumb) {
    filtersFromBreadCrumb = breadcrumb.filters;
  } else {
    filtersFromBreadCrumb = [];
  }

  if (!filtersFromBreadCrumb) {
    return;
  }

  // eslint-disable-next-line consistent-return
  return filtersFromBreadCrumb.find((item) => item.property === filterId);
}

export function setDrawingFromResults(data) {
  return (dispatch, getState) => {
    const state = getState();
    const key = keyifyParamsForFiltersRequest(state);

    dispatch(setDrawing(key, data));
  };
}

export function initializeStructureFilter(key, filter) {
  return (dispatch, getState) => {
    const state = getState();
    const filterId = filter.get('id');
    const filterFromBreadCrumb = getStructureFilterFromBreadCrumb(state, filterId);
    const drawing = drawingSelector(state) || null;

    if (drawing && drawing.fromEditor) {
      return dispatch(setStructureOptions(key, drawing.options));
    }

    if (!filterFromBreadCrumb) {
      const options = selectedOptionsNameValueObjectsArraySelector(state, {
        id: key,
      });

      dispatch(setStructureOptions(key, options));
      dispatch(setDrawingFromResults(drawing));
    } else {
      const { options, structure, structurePreview } = filterFromBreadCrumb;

      dispatch(setStructureOptions(key, options));
      dispatch(
        setDrawing(key, {
          structure,
          preview: structurePreview,
          fromBreadCrumb: true,
          options,
        })
      );
    }
    return null;
  };
}

export function setDrawingFromEditor(data, keyForStructureData) {
  return (dispatch) => {
    dispatch(setSelectedAndExpandedFilter(keyForStructureData));
    dispatch(
      setDrawing(keyForStructureData, {
        ...data,
        fromEditor: true,
      })
    );
  };
}

export function goToEditor() {
  return (dispatch, getState) => {
    const state = getState();
    const drawing = drawingSelector(state);
    const keyForStructureData = keyifyParamsForFiltersRequest(state);
    const { structureFilter: structureUID } = structureContainerIds;
    const options = selectedOptionsNameValueObjectsArraySelector(getState(), { id: keyForStructureData });
    const stateParams = getCombinedRouteParams(store.getState()) || {};
    StructureContainers.saveContainer(structureUID, {
      data: get(drawing, 'structure'),
      preview: get(drawing, 'preview'),
      options,
    });
    history.push({
      pathname: `/structure-editor/${structureUID}`,
      state: {
        target: 'results',
        structureUID,
        context: stateParams.context,
        structureData: drawing,
        keyForStructureData,
        previousDatabaseId: stateParams.databaseId,
      },
    });
  };
}

export function openStructureOptionsDialog() {
  return (dispatch, getState) => {
    const state = getState();
    const key = keyifyParamsForFiltersRequest(state);

    dispatch(
      updateStructureOptionsDialogAction({
        id: key,
      })
    );
  };
}

export function clearStructureFilter({ key, filter }) {
  return (dispatch, getState) => {
    const { structureFilter: structureUID } = structureContainerIds;

    // Remove from Redux state
    dispatch(setDrawingFromResults(null));
    dispatch(setStructureOptions(key));

    // Remove from structure container
    StructureContainers.removeContainerByUID(structureUID);

    // Remove from breadcrumb
    const databaseId = databaseIdSelector(getState());
    const breadcrumb = Breadcrumb.getActiveBreadcrumbForDb(databaseId);
    if (breadcrumb && breadcrumb.filters) {
      breadcrumb.filters = breadcrumb.filters.filter((item) => item.property !== filter.get('id'));
      Breadcrumb.updateBreadcrumbs(breadcrumb);
    }
  };
}
